"use client"

import type React from "react"
import { DoujinsPremiumModal } from "@/components/doujins-premium-modal"
import { StarRating } from "@/components/star-rating"
import { PlayheadReadout, PlayheadSeek, createPlayheadStore, type PlayheadStore } from "@/components/playhead"
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/dropdown-menu"
import { ContentScriptsPanel } from "@/components/content-scripts-panel"
import { BooruScriptMiniPlayer } from "@/components/marketplace/booru-script-mini-player"
import { DeviceXYPad } from "@/components/device-xy-pad"
import { QuickConnectDeviceModal } from "@/components/quick-connect-device-modal"
import type { UserScript, CommunityScript } from "@/lib/script-store"
import type { CommunityScript as MarketScript } from "@/lib/marketplace-store"
import { SuggestedTagsPanel } from "@/components/suggested-tags-panel"
import { SuggestedArtistsPanel } from "@/components/suggested-artists-panel"
import { DisputesPanel } from "@/components/disputes-panel"
import { CommentsPanel } from "@/components/comments-panel"
import { useTagProposals } from "@/lib/use-tag-proposals"
import { applyTagOverlay } from "@/lib/tag-proposals"
import { useFavoritesStore } from "@/lib/favorites-store"
import { useGlobalPlaybackStore } from "@/lib/global-playback-store"
import { useScriptLinkingStore } from "@/lib/script-linking-store"
import { ReportContentDialog } from "@/components/report-content-dialog"
import { usePipStore } from "@/lib/pip-store"
import { getImageUrl, getVideoCrossOrigin } from "@/lib/image-utils"
import { inferOrderMediaType } from "@/lib/order-media"
import { useBooruCredentialsStore } from "@/lib/booru-credentials-store"
import { useSourceStore } from "@/lib/source-store"
import { useScriptPlaybackStore } from "@/lib/script-playback-store"
import { categorizeTags } from "@/lib/tag-categorizer"
import { VROverlay } from "@/components/vr/vr-overlay"
import { vrXRStore } from "@/components/vr/vr-xr-store"
import { isVRPost } from "@/lib/vr-utils"
import { useVRStore } from "@/lib/vr-store"

import { useState, useEffect, useRef, useMemo, useCallback } from "react"
import Image from "next/image"
import { VideoPreroll } from "@/components/ads/VideoPreroll"
import { useAdTier, setModalAdTier } from "@/lib/ad-tier"
import { warmVideoPreroll } from "@/lib/ad-preroll-cache"
import { markRealAdWatchedToday } from "@/lib/skip-ads"
import { LetterboxAd } from "@/components/ads/LetterboxAd"
import { PillarboxAd } from "@/components/ads/PillarboxAd"
import { AlbumStripAdWrapper, useGalleryAdIndices } from "@/components/ads/AlbumStripAdWrapper"
import { MediaGalleryAdSlot } from "@/components/ads/MediaGalleryAdSlot"
import { InterstitialAd, useInterstitial, useHouseInterstitial } from "@/components/ads/InterstitialAd"
import { useAdStore } from "@/lib/ad-store"
import { useAdFrequencyStore } from "@/lib/ad-frequency-store"
import { markAdShown, isAdTestModeOn, isAdCooldownExpired } from "@/lib/ad-cooldown"
import { isGlobalAdsOff } from "@/lib/global-ads-off"
import { PauseOverlayAd } from "@/components/ads/PauseOverlayAd"
import {
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Info,
  Heart,
  Download,
  Share2,
  Play,
  Pause,
  Repeat,
  Repeat2,
  SkipBack,
  MessageCircle,
  VolumeX,
  Volume2,
  ScrollText,
  AlertCircle,
  Clock,
  X,
  Settings,
  GripVertical,
  Users,
  PlayCircle,
  Camera,
  FolderArchive,
  AlertTriangle,
  FolderPlus,
  Loader2,
  SquaresFour,
  PenTool,
  Folder,
  ZoomIn,
  ZoomOut,
  LayoutList,
  Aperture,
  Presentation,
  BookOpen,
  Spline,
  Grid3X3,
  SlidersHorizontal,
  WaveSine,
  HeartHandshake,
  Tags,
  Gem,
  Shuffle,
  PictureInPicture2,
  MoveVertical,
  Frame,
} from "@/lib/icons"
import type { BooruPost } from "@/lib/booru-api"
import { FindSourceDialog } from "@/components/booru-modal/find-source-dialog"
import type { SourceMatch } from "@/lib/reverse-search"
import { SaveToGoonfolioDialog } from "@/components/goonfolio/SaveToGoonfolioDialog"
import { useGooncaveStore } from "@/lib/gooncave-store"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Separator } from "@/components/ui/separator"
import { TagWithContextMenu } from "@/components/tag-with-context-menu"
import { cn } from "@/lib/utils"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Switch } from "@/components/ui/switch"
import { Label } from "@/components/ui/label"
import { Slider } from "@/components/ui/slider"
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet"
import { useSettingsStore } from "@/lib/settings-store"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { useUserRatingsStore } from "@/lib/user-ratings-store"
import { extractSimilarTags } from "@/lib/similar-finder"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs" // Import Tabs components
import { toast } from "sonner"
import { useTranslations } from "next-intl"
import { AlbumThumbnailStrip } from "@/components/booru-modal/album-thumbnail-strip"
import { VideoControlsOverlay } from "@/components/booru-modal/video-controls-overlay"
import { ZoomControls } from "@/components/booru-modal/zoom-controls"
import { AudioControlsBar } from "@/components/booru-modal/audio-controls-bar"
import { TopToolbar } from "@/components/booru-modal/top-toolbar"
import { MediaDisplayArea } from "@/components/booru-modal/media-display-area"
import { InfoPanelView } from "@/components/booru-modal/info-panel-view"
import { DeviceStatusIndicator } from "@/components/booru-modal/device-status-indicator"
import { useGutterAds } from "@/components/booru-modal/use-gutter-ads"
import { useSlideshow } from "@/components/booru-modal/use-slideshow"
import { useFrameCapture } from "@/components/booru-modal/use-frame-capture"
import { useModalDownloads } from "@/components/booru-modal/use-modal-downloads"
import { useModalAudio } from "@/components/booru-modal/use-modal-audio"
import { useGalleryContent } from "@/components/booru-modal/use-gallery-content"
import { usePostTagDetails } from "@/components/booru-modal/use-post-tag-details"
import { useMediaGestures } from "@/components/booru-modal/use-media-gestures"
import { useVideoPlayback } from "@/components/booru-modal/use-video-playback"
import { useVideoAdOverlays } from "@/components/booru-modal/use-video-ad-overlays"
import { useModalKeyboard } from "@/components/booru-modal/use-modal-keyboard"
import { useLazyMediaUrls } from "@/components/booru-modal/use-lazy-media-urls"
import { useModalDebugLogging } from "@/components/booru-modal/use-modal-debug-logging"
import { useVideoPreroll } from "@/components/booru-modal/use-video-preroll"
import { useModalScriptActions } from "@/components/booru-modal/use-modal-script-actions"
import { useAlbumNavigation } from "@/components/booru-modal/use-album-navigation"
import { useModalPostActions } from "@/components/booru-modal/use-modal-post-actions"
import { useMountedVideo } from "@/components/booru-modal/use-mounted-video"
import { AlbumNavBar } from "@/components/booru-modal/album-nav-bar"
import { AlbumPageCounter } from "@/components/booru-modal/album-page-counter"
import { PostNavArrows } from "@/components/booru-modal/post-nav-arrows"
import { PremiumContentAlert } from "@/components/booru-modal/premium-content-alert"
import { AlbumSideButtons } from "@/components/booru-modal/album-side-buttons"

const DEFAULT_MEDIA_VOLUME = 0.3

const BOORU_SCRIPT_PLAY_OPTIONS = { skipHSSP: false } as const

interface BooruDetailModalProps {
  post: BooruPost | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onNavigate?: (direction: "prev" | "next") => void
  canNavigate?: { prev: boolean; next: boolean }
  onTagClick?: (tag: string) => void
  onNegativeTagClick?: (tag: string) => void
  onOpenTagInNewSet?: (tag: string) => void
  /** Add several tags to the current set in one update (Find Similar) */
  onTagsAdd?: (tags: string[]) => void
  onOpenTagInBackgroundSet?: (tag: string) => void
  fullscreen?: boolean
  allPosts?: BooruPost[]
  bountySelectionActive?: boolean
  onAddToBounty?: (post: BooruPost) => void
  commissionSelectionActive?: boolean
  onAddToCommission?: (post: BooruPost) => void
  onOpenEditors?: () => void
}



// Fully self-contained — subscribes only to what it needs so the main modal
// never re-renders due to script playback state changes
function ScriptActiveOverlay({ hideUI }: { hideUI: boolean }) {
  const tt = useTranslations("booruDetailModal")
  const activeScriptId = useScriptPlaybackStore((s) => s.activeScriptId)
  const activeScriptName = useScriptPlaybackStore((s) => s.activeScriptName)
  const isPlaying = useScriptPlaybackStore((s) => s.isPlaying)
  const stopScript = useScriptPlaybackStore((s) => s.stopScript)
  const globalPlay = useGlobalPlaybackStore((s) => s.play)
  const globalPause = useGlobalPlaybackStore((s) => s.pause)
  const globalSetCurrentTime = useGlobalPlaybackStore((s) => s.setCurrentTime)
  const loop = useGlobalPlaybackStore((s) => s.loop)
  const setLoop = useGlobalPlaybackStore((s) => s.setLoop)

  if (hideUI || !activeScriptId || !activeScriptName) return null

  const btnClass = "h-6 w-6 p-0 rounded-full flex items-center justify-center hover:bg-white/20 transition-colors shrink-0"

  return (
    <div className="absolute top-16 left-1/2 -translate-x-1/2 z-30 pointer-events-auto max-w-[calc(100vw-2rem)]">
      <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-3 py-2 rounded-full shadow-lg flex items-center gap-1.5 min-w-0">
        <ScrollText className="h-3.5 w-3.5 shrink-0" />
        <span className="font-medium text-xs truncate min-w-0 max-w-[120px]">{activeScriptName}</span>

        {/* Restart */}
        <button type="button" title={tt("restart")} className={btnClass}
          onClick={(e) => { e.stopPropagation(); globalSetCurrentTime(0) }}>
          <SkipBack className="h-3 w-3" />
        </button>

        {/* Play / Pause */}
        <button type="button" title={isPlaying ? tt("pause") : tt("play")} className={btnClass}
          onClick={(e) => {
            e.stopPropagation()
            isPlaying ? globalPause() : globalPlay(BOORU_SCRIPT_PLAY_OPTIONS).catch(() => {})
          }}>
          {isPlaying ? <Pause className="h-3 w-3" /> : <Play className="h-3 w-3" />}
        </button>

        {/* Loop */}
        <button type="button" title={loop ? "Loop on" : "Loop off"} className={cn(btnClass, loop && "bg-white/25")}
          onClick={(e) => { e.stopPropagation(); setLoop(!loop) }}>
          <Repeat className="h-3 w-3" />
        </button>

        {/* Stop */}
        <button type="button" title={tt("stop")} className={btnClass}
          onClick={(e) => { e.stopPropagation(); stopScript() }}>
          <X className="h-3 w-3" />
        </button>
      </div>
    </div>
  )
}


export function BooruDetailModal({
  post,
  open,
  onOpenChange,
  onNavigate,
  canNavigate,
  onTagClick,
  onNegativeTagClick,
  onOpenTagInNewSet,
  onTagsAdd,
  onOpenTagInBackgroundSet,
  fullscreen = false,
  allPosts = [],
  bountySelectionActive = false,
  onAddToBounty,
  commissionSelectionActive = false,
  onAddToCommission,
  onOpenEditors,
}: BooruDetailModalProps) {
  const tt = useTranslations("booruDetailModal")
  const setVRMode = useVRStore((state) => state.setVRMode)
  const isVRMode = useVRStore((state) => state.isVRMode)
  // Version check - always log when modal opens
  if (open && post) {
    console.log('[BooruDetailModal v48] Modal opened for:', post.site, post.id, 'albumImages:', post.albumImages?.length, 'albumMediaCount:', post.albumMediaCount)
  }
  
  // Debug: Log when modal opens with a Doujins post
  if (open && post?.site === 'doujins') {
    console.log('[BooruDetailModal v48] Opening Doujins post:', {
      id: post.id,
      title: post.title,
      isPremium: post.isPremium,
      albumMediaCount: post.albumMediaCount,
      albumImagesLength: post.albumImages?.length,
      shouldShowPremiumModal: post.isPremium || post.albumMediaCount === 1 || (post.albumImages && post.albumImages.length === 1)
    })
  }

  const { isFavorite: checkIsFavorite, toggleFavorite } = useFavoritesStore()
  const { getCredentials } = useBooruCredentialsStore()
  const pauseBackgroundPlayback = useGlobalPlaybackStore((state) => state.pauseBackgroundPlayback)
  const setGlobalScript = useGlobalPlaybackStore((state) => state.setScript)
  const globalPlay = useGlobalPlaybackStore((state) => state.play)
  const globalPause = useGlobalPlaybackStore((state) => state.pause)
  const globalSetSyncDelay = useGlobalPlaybackStore((state) => state.setSyncDelay)
  // T6: VROverlay's Script tab needs the currently-active script (if any) and a way to pick
  // a different one — neither was passed before, so the VR Script tab always showed empty
  // regardless of whether a script was actually loaded for this post.
  const vrActiveScript = useGlobalPlaybackStore((state) => state.currentScript)
  const handleVrScriptSelect = useCallback((script: UserScript) => {
    setGlobalScript(script)
  }, [setGlobalScript])
  const { openPip } = usePipStore()
  
  // Only subscribe to what the modal itself needs — timing/page state is in ScriptActiveOverlay
  const activeScriptId = useScriptPlaybackStore((s) => s.activeScriptId)
  const activeScriptName = useScriptPlaybackStore((s) => s.activeScriptName)
  const connectedDeviceName = useScriptPlaybackStore((s) => s.connectedDeviceName)
  const isDeviceConnected = useScriptPlaybackStore((s) => s.isDeviceConnected)
  // The device status pill below was previously pure display -- "No device"
  // with nothing to click, forcing a trip back to the Devices tab to fix it
  // mid-viewing. Same lightweight connect flow used from the chat popover.
  const [showQuickConnect, setShowQuickConnect] = useState(false)
  // Ad tier for this post (1 only when its source is "safe"); shared with nested ad slots via a module value
  const modalAdTier = useAdTier(post ? [post] : [])
  setModalAdTier(modalAdTier)

  // Get bearer tokens for Sankaku sites (only if enabled)
  const { sources } = useSourceStore()
  const sankakuEnabled = sources.find(s => s.id === 'sankaku')?.enabled
  const idolSankakuEnabled = sources.find(s => s.id === 'idolsankaku')?.enabled
  const sankakuToken = sankakuEnabled ? getCredentials('sankaku')?.apiKey : undefined
  const idolSankakuToken = idolSankakuEnabled ? getCredentials('idolsankaku')?.apiKey : undefined
  
  
  // Extension cycling for videos that fail to load
  const [videoExtensionIndex, setVideoExtensionIndex] = useState(0)
  const [triedExtensions, setTriedExtensions] = useState<Set<string>>(new Set())
  const videoExtensions = ['.mp4', '.webm', '.mov', '.avi', '.mkv', '.m4v']
  
  const [showInfo, setShowInfo] = useState(false)
  const [zoom, setZoom] = useState(1)
  const [artistsTab, setArtistsTab] = useState("confirmed")
  const [seriesTab, setSeriesTab] = useState("confirmed")
  const [tagsTab, setTagsTab] = useState("general")
  const [artistsOpen, setArtistsOpen] = useState(false)
  const [seriesOpen, setSeriesOpen] = useState(false)
  const [tagsOpen, setTagsOpen] = useState(true)
  // Keep these for panels that still need collapse
  const [scriptsOpen, setScriptsOpen] = useState(false)
  const [scriptsActiveTab, setScriptsActiveTab] = useState("marketplace")
  const [disputesOpen, setDisputesOpen] = useState(false)
  // Remove: artistsOpen, charactersOpen, copyrightOpen, tagsOpen, suggestArtistsOpen, suggestTagsOpen
  const [hideUI, setHideUI] = useState(false)
  const [panX, setPanX] = useState(0)
  const [panY, setPanY] = useState(0)
  const [isPanning, setIsPanning] = useState(false)
  const [panStart, setPanStart] = useState({ x: 0, y: 0 })
  const [showReportDialog, setShowReportDialog] = useState(false)
  const [showSaveToGoonfolio, setShowSaveToGoonfolio] = useState(false)

  const [isPlaying, setIsPlaying] = useState(false)
  const [playbackSpeed, setPlaybackSpeed] = useState(1)
  const [isLooping, setIsLooping] = useState(false)
  // Playhead lives outside React state: it changes ~8-12x/sec during playback and a state
  // write re-rendered this whole ~5800-line modal each time (measured: 22 commits/s, 55%
  // of frames dropped). Only <PlayheadReadout>/<PlayheadSeek> subscribe to it.
  const playhead = useRef<PlayheadStore | null>(null)
  if (!playhead.current) playhead.current = createPlayheadStore()
  const getCurrentTime = () => playhead.current!.get()
  const setCurrentTime = (t: number) => playhead.current!.set(t)
  const [duration, setDuration] = useState(0)
  const [volume, setVolume] = useState(DEFAULT_MEDIA_VOLUME)
  const [isMuted, setIsMuted] = useState(false)
  const [isVideoZoomEnabled, setIsVideoZoomEnabled] = useState(false)
  const [showVolumePopup, setShowVolumePopup] = useState(false)
  const [showComments, setShowComments] = useState(false)
  
  // A-B Loop state
  const [abLoopEnabled, setAbLoopEnabled] = useState(false)
  const [loopPointA, setLoopPointA] = useState<number | null>(null)
  const [loopPointB, setLoopPointB] = useState<number | null>(null)
  
  // Frame navigation state
  const [frameNavEnabled, setFrameNavEnabled] = useState(false)
  
  const videoRef = useRef<HTMLVideoElement>(null)
  const [shouldFallbackToImage, setShouldFallbackToImage] = useState(false)
  const [imageDisplayMode, setImageDisplayMode] = useState<'contain' | 'cover'>('contain')
  const mediaRootRef = useRef<HTMLDivElement>(null)
  const topToolbarRef = useRef<HTMLDivElement>(null)
  const volumePopupRef = useRef<HTMLDivElement>(null)
  const animationFrameRef = useRef<number | undefined>(undefined)
  const lastTimeUpdateRef = useRef(0)
  const pendingPlayRef = useRef<boolean>(false)
  const canPlayListenerRef = useRef<(() => void) | null>(null)
  // Slideshow on/off, shuffle, interval and the auto-advance timer: components/booru-modal/use-slideshow.ts
  const {
    isSlideshowActive, setIsSlideshowActive, slideshowShuffle, setSlideshowShuffle, slideshowInterval, setSlideshowInterval,
  } = useSlideshow({ open, post, onNavigate, canNavigate })
  const usedIndicesRef = useRef<Set<number>>(new Set())
  const userChangedMuteRef = useRef(false)
  const previousModalPostKeyRef = useRef<string | null>(null)

  const bottomSwipeStartYRef = useRef<number | null>(null)

  useEffect(() => {
    const postKey = open && post?.id ? `${post.site ?? "unknown"}:${post.id}` : null
    const switchedPost = !!postKey && !!previousModalPostKeyRef.current && previousModalPostKeyRef.current !== postKey

    if (!postKey || switchedPost) {
      setVRMode(false)
      try {
        vrXRStore.getState().session?.end().catch(() => {})
      } catch {}
    }

    previousModalPostKeyRef.current = postKey

    if (!postKey) return
    setVolume(DEFAULT_MEDIA_VOLUME)
    setAudioVolume(DEFAULT_MEDIA_VOLUME)
    setIsMuted(false)
    setIsAudioMuted(false)
    setShowVolumePopup(false)
    userChangedMuteRef.current = false
  }, [open, post?.id, post?.site, setVRMode])

  const handleBottomSwipeTouchStart = (e: React.TouchEvent) => {
    bottomSwipeStartYRef.current = e.touches[0].clientY
  }
  const handleBottomSwipeTouchEnd = (e: React.TouchEvent) => {
    if (bottomSwipeStartYRef.current === null) return
    const dy = bottomSwipeStartYRef.current - e.changedTouches[0].clientY
    bottomSwipeStartYRef.current = null
    if (dy > 50) onOpenChange(false)
  }

  const [showPremiumModal, setShowPremiumModal] = useState(false)
  // Letterbox/pillarbox ad state. mediaNativeAR is set from either the
  // video's onLoadedMetadata or the image's onLoad — whichever is showing —
  // so both ad surfaces work for images too (H7), not just video.
  const [mediaNativeAR, setMediaNativeAR] = useState<number | null>(null)
  const [containerAR, setContainerAR] = useState<number | null>(null)
  const [containerH, setContainerH] = useState<number | null>(null)
  const longPressTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined)


  const [showScriptPlayer, setShowScriptPlayer] = useState(false)
  const [activeScript, setActiveScript] = useState<UserScript | CommunityScript | null>(null)
  const [inlineMarketScript, setInlineMarketScript] = useState<CommunityScript | null>(null)
  const [showDeviceXYPad, setShowDeviceXYPad] = useState(() => isDeviceConnected)
  const { isLinkingMode, selectPostForScript } = useScriptLinkingStore()
  const linkingTargetIsGooncave = useScriptLinkingStore((s) => s.returnToTab === "Gooncave")

  const [currentAlbumPage, setCurrentAlbumPage] = useState(0)
  // Gallery ad slot currently occupying the main viewer, as a genuine item you
  // page past (not overlaid on top of content) — see handleAlbumNextPage below.
  // Value is the ad's virtual slot index from useGalleryAdIndices, or null.
  const [viewingAdSlot, setViewingAdSlot] = useState<number | null>(null)
  const albumThumbnailsRef = useRef<HTMLDivElement>(null)
  const albumStripRef = useRef<HTMLDivElement>(null)
  const [albumStripHeight, setAlbumStripHeight] = useState(0)
  
  // Real pages for Erome placeholder posts and Doujins galleries opened without pages: components/booru-modal/use-gallery-content.ts
  const { fetchedDoujinsPages, fetchedDoujinsTitle, isResolvingGalleryContent } = useGalleryContent({ open, post, setCurrentAlbumPage })

  const {
    imageNavDirection,
    infoSwipeDirection,
    handMode,
    mouseWheelBehavior,
    autoDownloadWithoutDialog,
    videoPitchCorrection,
    setVideoPitchCorrection,
    videoAutoMute,
    videoDefaultSpeed,
    autoplayVideos,
    // Settings panel has real "Mouse Pan" / "Pan Sensitivity" controls
    // (settings-panel.tsx) that wrote to these store fields correctly —
    // this component just never read them, using hardcoded local
    // `const enableMousePan = true` / `const panSensitivity = 1` instead,
    // so the settings silently did nothing. Fixed 2026-08-19.
    enableMousePan,
    panSensitivity,
    booruModalLayout,
    eromeModalPositioning,
    imageQuality,
  } = useSettingsStore()

  const { getRating, setRating } = useUserRatingsStore()

  // Real, shared tag-proposal voting (see lib/use-tag-proposals.ts) — used to
  // be entirely localStorage-only (lib/suggested-tags-store.ts), so a dispute
  // or vote never left the browser it was made in.
  const tagContentId = post?.id ? `${post.site}:${post.id}` : null
  const tagProposals = useTagProposals(tagContentId)
  const disputedTags = tagProposals.overlay.hidden

  const handleTagDispute = (tag: string, reason: string) => {
    void tagProposals.disputeTag(tag, reason).then((result) => {
      if (!result.ok && result.error === "sign-in-required") {
        toast.error(tt("signInToDisputeTags"))
      } else if (!result.ok) {
        toast.error(result.error || tt("somethingWentWrong"))
      }
    })
  }

  const currentRating = post && post.site ? getRating(post.site, String(post.id)) : 0

  const effectiveAlbumImages = useMemo(() => {
    const rawImages = fetchedDoujinsPages ?? post?.albumImages ?? []
    return Array.isArray(rawImages)
      ? rawImages.filter((url): url is string => typeof url === "string" && url.trim().length > 0)
      : []
  }, [fetchedDoujinsPages, post?.albumImages])
  const singleFetchedMediaUrl = fetchedDoujinsPages !== null && effectiveAlbumImages.length === 1 ? effectiveAlbumImages[0] : null
  const isAlbum = effectiveAlbumImages.length > 1
  const albumPages = effectiveAlbumImages
  const totalAlbumPages = albumPages.length

  // Gallery ads for albums: computed as virtual indices interleaved with real
  // pages (see components/ads/AlbumStripAdWrapper.tsx — already used to draw
  // the thumbnail-strip ad slots). `adBeforeRealPage` maps a real page index
  // to the ad virtual-slot that should be shown as its own item right before
  // the user reaches that real page, so ads are something you page past, not
  // an overlay on top of real content.
  const { adIndices: albumAdIndices } = useGalleryAdIndices(totalAlbumPages)
  const adBeforeRealPage = useMemo(() => {
    const map = new Map<number, number>()
    if (albumAdIndices.size === 0) return map
    let realIdx = 0
    const virtualCount = totalAlbumPages + albumAdIndices.size
    for (let vIdx = 0; vIdx < virtualCount; vIdx++) {
      if (albumAdIndices.has(vIdx)) {
        map.set(realIdx, vIdx)
      } else {
        realIdx++
      }
    }
    return map
  }, [albumAdIndices, totalAlbumPages])

  // Video pre-roll: show ad every N videos for non-premium users
  const { isPremiumUser } = useAdStore()
  // "Force Ads" test mode's whole documented purpose (AdTestPanel.tsx) is
  // bypassing every frequency/cooldown check so an admin can verify ads
  // render without waiting around — but every ad gate in this file kept
  // checking the raw `isPremiumUser` directly, so a premium account (the
  // kind an admin actually tests with) hit a hard block Force Ads could
  // never override: video preroll, letterbox/pillarbox, and the pause
  // overlay all silently never fired, reported live as "video ads don't
  // work at all even with Force Ads on." booru-grid.tsx and every other ad
  // component already derive this same override; this file just never did.
  const effectivePremiumUser = (isPremiumUser || isGlobalAdsOff()) && !isAdTestModeOn()

  // Full-screen interstitial: shown between post opens (image posts only — videos already get a preroll).
  // Network (ExoClick/ClickAdilla) and house-ad interstitials are fully
  // independent schedules now — see the kind-split comment in
  // components/ads/InterstitialAd.tsx near keysFor(). They're still mutually
  // exclusive on-screen (only one full-screen overlay makes sense at once):
  // house gets priority if a single post open would trigger both, since it's
  // the site's own message rather than paid inventory.
  const { showInterstitial, recordPostOpen, dismissInterstitial } = useInterstitial()
  const { showInterstitial: showHouseInterstitial, recordPostOpen: recordHousePostOpen, dismissInterstitial: dismissHouseInterstitial } = useHouseInterstitial()


  // Detect content type - GIFs are treated as videos (same thing)
  const hasGifTag = post?.rawPost?.hasGifTag
  const primaryMediaUrl = singleFetchedMediaUrl || post?.fileUrl || ""
  const isVideo =
    !isAlbum &&
    (inferOrderMediaType(primaryMediaUrl, post?.type) === "video" || !!post?.rawPost?.embedUrl)
  // Per-source media URLs fetched when a post opens (Rule34 video, Rule34Hentai full image, XlecX video, JavApi player):
  // components/booru-modal/use-lazy-media-urls.ts
  const { lazyLoadedImageUrl, lazyLoadedVideoUrl, lazyLoadedEmbedUrl, isLoadingFullImage, rule34VideoUrl } = useLazyMediaUrls({ open, post, isVideo })
  // Video preroll: admin frequency settings, deciding when one shows, and its resolved VAST: components/booru-modal/use-video-preroll.ts
  const { showVideoPreroll, setShowVideoPreroll, prerollVastUrl, prerollZoneId, prerollPreresolved } = useVideoPreroll({
    open, isVideo, effectivePremiumUser, modalAdTier, postId: post?.id,
  })
  
  const currentAlbumImageUrl = isAlbum ? albumPages[currentAlbumPage] : null
  const isCurrentAlbumPageVideo = !!currentAlbumImageUrl && inferOrderMediaType(currentAlbumImageUrl, post?.type) === "video"
  
  const isMediaVideo = !!(isVideo || isCurrentAlbumPageVideo)
  
  const isErome = post?.site === "erome"
  const isEromeVideo = isErome && (isVideo || !!isCurrentAlbumPageVideo)
  const modalDialogLayout =
    booruModalLayout === "fullscreen"
      ? "fullscreen"
      : isErome
        ? eromeModalPositioning === "centered"
          ? "classic"
          : eromeModalPositioning === "pinned"
            ? "pinned"
            : isEromeVideo
              ? "pinned"
              : "classic"
        : "classic"

  const isAlbumVideo = !!(isAlbum && isCurrentAlbumPageVideo)
  const allowVideoZoom = !isMediaVideo || isVideoZoomEnabled
  // Excludes showVideoPreroll/showInterstitial/viewingAdSlot: the real
  // video is paused/hidden behind whichever of those is covering it, so its
  // own playback controls control the wrong thing right now. Previously
  // this rendered unconditionally whenever a video post had UI visible — at
  // z-20, above the in-stream pre-roll's z-10 — so the real HUD's seek bar
  // and control row sat directly on top of the ad's own Skip Ad button,
  // reported live as "skip ad button is under the play bar".
  const showVideoHud = !hideUI && isMediaVideo && !showVideoPreroll && !showInterstitial && viewingAdSlot === null
  const showAlbumStrip = !hideUI && isAlbum
  const videoHudBottomPx = showAlbumStrip ? (albumStripHeight > 0 ? albumStripHeight : 66) : 0
  const showAlbumNav = !hideUI && isAlbum && !isCurrentAlbumPageVideo
  const albumNavBottomPx = showAlbumStrip ? (albumStripHeight > 0 ? albumStripHeight + 16 : 76) : 16

  const getMediaUrl = (url?: string | null, isFullSize: boolean = false) => {
    if (!url) return ""
    
    // Get bearer token for Sankaku sites
    const bearerToken = post?.site === 'sankaku' ? sankakuToken : post?.site === 'idolsankaku' ? idolSankakuToken : undefined
    
    // Use the standard image utils for all sites with quality preference
    return getImageUrl(url, { isFullSize, postId: post?.id, site: post?.site, bearerToken })
  }
  
  // Get the best URL based on user's quality preference
  const getPreferredMediaUrl = () => {
    if (!post) return ""
    
    const bearerToken = post.site === 'sankaku' ? sankakuToken : post.site === 'idolsankaku' ? idolSankakuToken : undefined
    
    let url: string | undefined
    
    // Check if this is a GIF - if so, prefer full quality to preserve animation
    const isGifFile = post.fileUrl?.toLowerCase().includes('.gif') || post.sampleUrl?.toLowerCase().includes('.gif')
    
    // Use lazy loaded URL for Rule34Hentai if available
    if (post.site === 'rule34hentai' && lazyLoadedImageUrl) {
      url = lazyLoadedImageUrl
    } else if (singleFetchedMediaUrl) {
      url = singleFetchedMediaUrl
    } else if (imageQuality === "full" || isGifFile) {
      url = post.fileUrl || post.sampleUrl || post.previewUrl
    } else if (imageQuality === "sample") {
      url = post.sampleUrl || post.fileUrl || post.previewUrl
    } else {
      url = post.previewUrl || post.sampleUrl || post.fileUrl
    }
    
    return getImageUrl(url, { isFullSize: imageQuality === "full" || isGifFile, postId: post.id, site: post.site, bearerToken })
  }
  
  // Helper function to swap video extension
  const getVideoUrlWithExtension = (url: string, extensionIndex: number): string => {
    if (!url) return url
    
    // Remove existing extension
    const urlWithoutExtension = url.replace(/\.(mp4|webm|mov|avi|mkv|flv|wmv|m4v|3gp|ogv)(\?.*)?$/i, '')
    
    // Add new extension
    const newExtension = videoExtensions[extensionIndex % videoExtensions.length]
    
    // Preserve query parameters if they exist
    const queryMatch = url.match(/(\?.*)$/)
    const queryParams = queryMatch ? queryMatch[1] : ''
    
    return `${urlWithoutExtension}${newExtension}${queryParams}`
  }
  
  // Get the current video URL with extension cycling applied (ONLY for XlecX)
  const getCurrentVideoUrl = (): string => {
    let baseUrl = ''
    
    // Rule34 uses special fetched URL
    if (post?.site === 'rule34' && rule34VideoUrl) {
      return rule34VideoUrl
    }
    
    // XlecX uses lazy loaded URL with extension cycling
    if (post?.site === 'xlecx') {
      if (lazyLoadedVideoUrl) {
        // XlecX video URLs are already full URLs from xlecx.shop - don't process them
        baseUrl = lazyLoadedVideoUrl
        console.log(`[v0] 🎥 Using XlecX lazy loaded video URL (direct): ${baseUrl}`)
      } else if (post?.fileUrl && inferOrderMediaType(post.fileUrl, post.type) === "video") {
        baseUrl = getMediaUrl(post.fileUrl)
        console.log(`[v0] 🎥 Using XlecX thumbnail URL (temporary): ${baseUrl}`)
      }
      
      // Apply extension cycling ONLY for XlecX
      if (videoExtensionIndex > 0 && baseUrl) {
        const cycledUrl = getVideoUrlWithExtension(baseUrl, videoExtensionIndex)
        console.log(`[v0] 🔄 XlecX extension cycling: attempt ${videoExtensionIndex + 1}, trying: ${cycledUrl}`)
        return cycledUrl
      }
      
      return baseUrl
    }
    
    const fallbackVideoUrl = isAlbum && isCurrentAlbumPageVideo ? currentAlbumImageUrl : primaryMediaUrl
    if (fallbackVideoUrl && inferOrderMediaType(fallbackVideoUrl, post?.type) === "video") {
      return getMediaUrl(fallbackVideoUrl)
    }
    
    return ''
  }

  const activeVideoUrl = useMemo(() => {
    if (isAlbum && isCurrentAlbumPageVideo && currentAlbumImageUrl) return currentAlbumImageUrl
    if (isMediaVideo && primaryMediaUrl) return primaryMediaUrl
    return null
  }, [currentAlbumImageUrl, isAlbum, isCurrentAlbumPageVideo, isMediaVideo, primaryMediaUrl])

  const [isVideoLoading, setIsVideoLoading] = useState(false)
  const [videoError, setVideoError] = useState<string | null>(null)
  const [vrSyncDelay, setVrSyncDelay] = useState(0)
  // T6: vrSyncDelay was local state, passed into VROverlay's slider but never forwarded to
  // the global playback engine that actually drives the device — dragging it did nothing.
  // Same meaning as every other surface (and app/vr/page.tsx): positive delays the device, negative makes it
  // fire earlier. It used to be inverted, so a "Script delay" of +100 ms fired the device EARLIER.
  useEffect(() => {
    globalSetSyncDelay(vrSyncDelay)
  }, [globalSetSyncDelay, vrSyncDelay])

  // Cleanup video state when URL changes
  useEffect(() => {
    console.log("[video-play] Video URL changed", {
      activeVideoUrl,
      pendingPlay: pendingPlayRef.current,
      hasListener: !!canPlayListenerRef.current,
    })
    
    // Reset state when video changes
    pendingPlayRef.current = false
    setIsVideoLoading(false)
    setVideoError(null)
    
    // Reset extension cycling when video changes
    setVideoExtensionIndex(0)
    setTriedExtensions(new Set())

    return () => {
      // Clean up listener on unmount
      if (canPlayListenerRef.current && videoRef.current) {
        console.log("[video-play] Cleaning up canplay listener on unmount")
        videoRef.current.removeEventListener("canplay", canPlayListenerRef.current)
        canPlayListenerRef.current = null
      }
    }
  }, [activeVideoUrl])


  useEffect(() => {
    if (!showVolumePopup) return
    const handlePointerDown = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node | null
      if (!target) return
      if (volumePopupRef.current?.contains(target)) return
      setShowVolumePopup(false)
    }
    window.addEventListener("mousedown", handlePointerDown)
    window.addEventListener("touchstart", handlePointerDown)
    return () => {
      window.removeEventListener("mousedown", handlePointerDown)
      window.removeEventListener("touchstart", handlePointerDown)
    }
  }, [showVolumePopup])

  useEffect(() => {
    // Find the index of the current post in the albumImages array
    if (isAlbum && post?.fileUrl && albumPages.length > 0) {
      const index = albumPages.findIndex(url => url === post.fileUrl)
      if (index !== -1) {
        setCurrentAlbumPage(index)
      } else {
        setCurrentAlbumPage(0)
      }
    } else {
      setCurrentAlbumPage(0)
    }
    setShouldFallbackToImage(false) // Reset fallback state for new post
    setImageDisplayMode('contain') // Reset display mode for new post
    setViewingAdSlot(null) // Reset gallery-ad viewer state for new post
  }, [post?.id, post?.fileUrl, isAlbum, albumPages])

  // Clear A-B loop points when post changes or modal closes
  useEffect(() => {
    if (!open || !post?.id) {
      setShowInfo(false)
      setShowComments(false)
      setShowReportDialog(false)
      // Clear loop points when modal closes or no post
      setLoopPointA(null)
      setLoopPointB(null)
      setAbLoopEnabled(false)
      // Reset duration and time
      setDuration(0)
      setCurrentTime(0)
      setInlineMarketScript(null)
    } else {
      // Clear loop points when switching to a different post
      setLoopPointA(null)
      setLoopPointB(null)
      // Reset duration and time for new post
      setDuration(0)
      setCurrentTime(0)
      setInlineMarketScript(null)
    }
  }, [open, post?.id])

  // Erome album / layout debug logging (only when localStorage booruAlbumDebug / booruLayoutDebug is "1"):
  // components/booru-modal/use-modal-debug-logging.ts
  useModalDebugLogging({ open, post, isAlbum, albumPages, activeVideoUrl, isVideo, isCurrentAlbumPageVideo, isVideoLoading, mediaRootRef, topToolbarRef, videoRef })

  useEffect(() => {
    if (!isMediaVideo) return
    if (activeVideoUrl) {
      setIsVideoZoomEnabled(false)
      setShowVolumePopup(false)
      setZoom(1)
      setPanX(0)
      setPanY(0)
    }
  }, [activeVideoUrl, isMediaVideo])

  useEffect(() => {
    if (!post || !post.site) return
    checkIsFavorite(`${post.site}-${post.id}`)
  }, [post, checkIsFavorite])

  // Full-screen interstitial: single image posts only.
  //  - videos already get a pre-roll, don't stack both
  //  - albums/galleries already get their own in-sequence gallery ad slots
  //    (see MediaGalleryAdSlot), so firing a full-screen interstitial on top
  //    of that double-dips the same view: reported live as a "NO WAY" ad
  //    covering the gallery the moment it was opened.
  useEffect(() => {
    if (!open || isVideo || isAlbum || !post?.id) return
    // House gets first crack at this post open — see the comment on the two
    // hooks above. Only consult the network schedule if house didn't fire,
    // so the two never both trigger (and stack) off the same click.
    const houseFired = recordHousePostOpen()
    if (!houseFired) recordPostOpen()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, post?.id, isVideo, isAlbum])


  // Keep the media container's measured geometry current, for
  // letterbox/pillarbox ad sizing. This used to be a useEffect that ran
  // `document.querySelector("[data-media-container]")` on [open, post?.id,
  // isAlbum, effectivePremiumUser] — but Radix Dialog portals its content,
  // so the div isn't actually in the DOM yet at the instant this effect's
  // callback runs on open. The querySelector came back null, the effect
  // bailed, and neither containerAR nor containerH were EVER set for that
  // view — meaning letterbox/pillarbox silently never rendered at all,
  // regardless of whether an ad zone existed, no matter how long you waited
  // (confirmed live: the fetch succeeded with real embed data, but the
  // component never mounted). A callback ref fires exactly when the node
  // itself mounts, sidestepping the portal-timing race entirely — same
  // pattern NetworkDisplayAd.tsx already uses for its own column-width
  // measurement.
  const mediaContainerRoRef = useRef<ResizeObserver | null>(null)
  const setMediaContainerRef = useCallback((el: HTMLDivElement | null) => {
    mediaContainerRoRef.current?.disconnect()
    mediaContainerRoRef.current = null
    if (!el) return
    const measure = () => {
      if (el.offsetWidth > 0 && el.offsetHeight > 0) {
        setContainerAR(el.offsetWidth / el.offsetHeight)
        setContainerH(el.offsetHeight)
      }
    }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    mediaContainerRoRef.current = ro
  }, [])

  // Letterbox/pillarbox gutter ads (fetch + dismissed state) live in components/booru-modal/use-gutter-ads.ts.
  const {
    letterboxAdDismissed, setLetterboxAdDismissed, letterboxAdData, letterboxTopAdData,
    pillarboxAdDismissed, setPillarboxAdDismissed, pillarboxAdData, pillarboxLeftAdData,
  } = useGutterAds({ open, isAlbum, effectivePremiumUser, postId: post?.id, modalAdTier, onReset: () => setMediaNativeAR(null) })

  // Auto-show premium modal for Doujins premium content
  useEffect(() => {
    if (!open || !post) {
      // Reset premium modal when detail modal closes
      setShowPremiumModal(false)
      return
    }
    
    // For Doujins galleries, check if it's premium
    if (post.site === 'doujins') {
      // Check multiple conditions for premium detection
      const hasOneAlbumImage = post.albumImages && post.albumImages.length === 1
      const hasOneMediaCount = post.albumMediaCount === 1
      const isPremiumFlag = post.isPremium === true
      
      const isPremiumGallery = isPremiumFlag || hasOneMediaCount || hasOneAlbumImage
      
      console.log('[BooruDetailModal v48] Doujins gallery opened:', {
        id: post.id,
        isPremium: post.isPremium,
        albumMediaCount: post.albumMediaCount,
        albumImagesLength: post.albumImages?.length,
        hasOneAlbumImage,
        hasOneMediaCount,
        isPremiumFlag,
        willShowPremiumModal: isPremiumGallery
      })
      
      // Show premium modal for any gallery with only 1 image
      if (isPremiumGallery) {
        const timer = setTimeout(() => {
          console.log('[BooruDetailModal v48] AUTO-SHOWING PREMIUM MODAL NOW for post:', post.id)
          setShowPremiumModal(true)
        }, 400)
        return () => clearTimeout(timer)
      }
    }
  }, [open, post?.id, post?.site, post?.isPremium, post?.albumMediaCount, post?.albumImages])

  useEffect(() => {
    if (albumThumbnailsRef.current && isAlbum) {
      // NOT children[currentAlbumPage] — the strip interleaves ad-slot
      // thumbnails among the real ones (AlbumStripAdWrapper), so a real
      // page's DOM position drifts further from its own page number the
      // more ad slots precede it in the virtual sequence. Positional
      // indexing was scrolling an earlier, wrong thumbnail into view —
      // harmless-looking on early pages (0-1 ad slots ahead of them so far)
      // but increasingly wrong deeper into the album, worst at the last
      // page, where every ad slot in the whole album precedes it. The
      // highlight border was never affected (it compares real page numbers
      // directly, not DOM position), so the CORRECT thumbnail always lit up
      // — it just wasn't the one being scrolled to, reported as "the
      // selected one doesn't move all the way into frame."
      const thumbnail = albumThumbnailsRef.current.querySelector<HTMLElement>(
        `[data-real-page="${currentAlbumPage}"]`,
      )
      if (thumbnail) {
        thumbnail.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" })
      }
    }
  }, [currentAlbumPage, isAlbum])

  useEffect(() => {
    if (!showAlbumStrip) {
      setAlbumStripHeight(0)
      return
    }
    const el = albumStripRef.current
    if (!el) return

    const update = () => setAlbumStripHeight(el.getBoundingClientRect().height)
    update()

    const ro = new ResizeObserver(() => update())
    ro.observe(el)
    return () => ro.disconnect()
  }, [showAlbumStrip])

  // Video playback engine (play/pause loop, canplay wait, A-B loop, VR time re-sync, auto-mute, video <-> script sync,
  // play and frame-step handlers): components/booru-modal/use-video-playback.ts. Called HERE on purpose: its effects must
  // keep running after the "video URL changed" cleanup effect above, exactly as before.
  // The mounted <video>, so the listeners below re-attach when it actually appears (components/booru-modal/use-mounted-video.ts)
  const videoEl = useMountedVideo(videoRef, isMediaVideo, activeVideoUrl)
  const { videoSyncSuppressRef, handlePlayPause, handleFrameForward, handleFrameBackward } = useVideoPlayback({
    videoEl,
    abLoopEnabled,
    activeVideoUrl,
    animationFrameRef,
    canPlayListenerRef,
    duration,
    getCurrentTime,
    globalPause,
    globalPlay,
    isLooping,
    isMediaVideo,
    isMuted,
    isPlaying,
    isVRMode,
    isVideo,
    lastTimeUpdateRef,
    loopPointA,
    loopPointB,
    pauseBackgroundPlayback,
    pendingPlayRef,
    playbackSpeed,
    post,
    setCurrentTime,
    setIsMuted,
    setIsPlaying,
    setIsVideoLoading,
    setPlaybackSpeed,
    setVideoError,
    showScriptPlayer,
    userChangedMuteRef,
    videoAutoMute,
    videoPitchCorrection,
    videoRef,
    volume,
  })

  // Pause-overlay ad and the gutter-ad dimming while a video plays: components/booru-modal/use-video-ad-overlays.ts
  const { showPauseOverlayAd, setShowPauseOverlayAd, letterboxFadeClass, pillarboxFadeClass } = useVideoAdOverlays({
    videoEl,
    postId: post?.id, videoRef, isMediaVideo, activeVideoUrl, effectivePremiumUser, showVideoPreroll, showInterstitial, viewingAdSlot, isVRMode,
  })

  // Doujins.com audio galleries (AudioManager, audio mode, progress, audio-synced slideshow): components/booru-modal/use-modal-audio.ts
  const {
    audioManager, isAudioMode, setIsAudioMode, audioVolume, setAudioVolume, isAudioMuted, setIsAudioMuted,
    audioProgress, hasAudio, isAudioPlaying, setIsAudioPlaying,
  } = useModalAudio({ post, initialVolume: DEFAULT_MEDIA_VOLUME, isSlideshowActive, setIsSlideshowActive, canNavigate, onNavigate })

  // Album page stepping and gallery-ad slots: components/booru-modal/use-album-navigation.ts
  const { handleAlbumPrevPage, handleAlbumNextPage, handleAlbumPageClick, handleAlbumAdSlotClick } = useAlbumNavigation({
    viewingAdSlot, setViewingAdSlot, currentAlbumPage, setCurrentAlbumPage, totalAlbumPages, adBeforeRealPage,
  })

  // Wheel zoom, pinch, pan, swipe navigation and long-press: components/booru-modal/use-media-gestures.ts
  const {
    handleWheel, handleTouchStart, handleTouchMove, handleTouchEnd, handleZoomIn, handleZoomOut,
    handleMediaLongPressEnd, handleMouseDown, handleMouseUp,
  } = useMediaGestures({
    allowVideoZoom,
    canNavigate,
    currentAlbumPage,
    enableMousePan,
    handMode,
    handleAlbumNextPage,
    handleAlbumPrevPage,
    imageDisplayMode,
    imageNavDirection,
    isAlbum,
    isMediaVideo,
    isPanning,
    isVideoZoomEnabled,
    longPressTimeoutRef,
    mediaRootRef,
    mouseWheelBehavior,
    onNavigate,
    onOpenChange,
    panSensitivity,
    panStart,
    panX,
    panY,
    post,
    setHideUI,
    setIsPanning,
    setPanStart,
    setPanX,
    setPanY,
    setShowInfo,
    setShowSaveToGoonfolio,
    setZoom,
    showInfo,
    totalAlbumPages,
    viewingAdSlot,
    zoom,
  })

  const [isAddToScriptsDropdownOpen, setIsAddToScriptsDropdownOpen] = useState(false)
  const [isGoonfolioDropdownOpen, setIsGoonfolioDropdownOpen] = useState(false)
  const [isFindSimilarDropdownOpen, setIsFindSimilarDropdownOpen] = useState(false)

  // Favorite, share and find-similar: components/booru-modal/use-modal-post-actions.ts
  const { handleFavoriteClick, handleShare, handleFindSimilar } = useModalPostActions({
    post, allPosts, toggleFavorite, onOpenTagInNewSet, onTagsAdd, onTagClick, setIsFindSimilarDropdownOpen,
  })

  // Hand-offs to the script editors, bounties and commissions, the PiP player and the XY pad:
  // components/booru-modal/use-modal-script-actions.ts
  const {
    getEnrichedPost, closeModalSurfaces, openEditorsAfterSelection, handleAddToScript, handleAddToVerticalEditor, handleOpenPip, handlePlayScript, handleCreateScript,
    handleManualControl, handleRequestBounty, handleAddToBountySelection, handleAddToCommissionSelection,
  } = useModalScriptActions({
    post, isAlbum, currentAlbumImageUrl, isMediaVideo, activeVideoUrl, effectiveAlbumImages,
    setShowInfo, setShowComments, setShowReportDialog, setInlineMarketScript, onOpenChange, onOpenEditors,
    onAddToBounty, onAddToCommission, selectPostForScript, openPip, setActiveScript, setShowScriptPlayer,
    setGlobalScript, globalPlay, isDeviceConnected, setShowQuickConnect, setShowDeviceXYPad,
  })

  // Keyboard shortcuts (arrows, Escape, I, zoom keys, Space): components/booru-modal/use-modal-keyboard.ts
  useModalKeyboard({
    open,
    isAlbum,
    viewingAdSlot,
    currentAlbumPage,
    totalAlbumPages,
    handleAlbumPrevPage,
    handleAlbumNextPage,
    onNavigate,
    canNavigate,
    onOpenChange,
    showInfo,
    setShowInfo,
    setZoom,
    setPanX,
    setPanY,
    isMediaVideo,
    isVideo,
    videoRef,
    setIsPlaying,
  })

  useEffect(() => {
    setZoom(1)
    setPanX(0)
    setPanY(0)
    setIsPlaying(false)
    setCurrentTime(0)
    // Playback speed was a local useState(1) that never read the "Video
    // Default Speed" setting at all — the slider in Settings had zero
    // effect on actual playback. Seeded here so every new video starts at
    // the configured default; setPlaybackSpeed elsewhere still lets the
    // user override it for the current video same as before.
    setPlaybackSpeed(videoDefaultSpeed)
    // Reset mute state tracking on new post so user can manually change mute again
    userChangedMuteRef.current = false
    // Clear captured frame when navigating to a new post
    resetCapture()
    resetTagDetails()
  }, [post?.id, videoDefaultSpeed])

  // Rule34 tag details (types for the tag groups) and writing known tag types into the local tag memory:
  // components/booru-modal/use-post-tag-details.ts
  const { rule34DetailTags, rule34DetailCategories, resetTagDetails } = usePostTagDetails({ open, post })

  const groupTags = (tags: string[] | undefined) => {
    if (!Array.isArray(tags)) {
      return { artists: [], characters: [], copyright: [], general: [] }
    }
    
    // Use the universal tag categorization system
    // Pass tagCategories from the post if available (from booru scraping)
    const categorized = categorizeTags(tags, rule34DetailCategories || post?.tagCategories)
    
    return {
      artists: categorized.artists,
      characters: categorized.characters,
      copyright: [...categorized.copyright, ...categorized.series],
      general: categorized.general,
    }
  }

  const rawTagsArray = rule34DetailTags || (Array.isArray(post?.tags)
    ? post.tags
    : post?.rawPost?.tags
      ? Array.isArray(post.rawPost.tags)
        ? post.rawPost.tags
        : Object.keys(post.rawPost.tags)
      : [])
  // Community-approved additions/removals overlaid on the source booru's own
  // tags (Goonru-side only — never edits the external booru). See
  // lib/use-tag-proposals.ts.
  const tagsArray = applyTagOverlay(rawTagsArray, tagProposals.overlay)
  const { artists, characters, copyright, general } = groupTags(tagsArray)

  const sheetSide = handMode === "right" ? "left" : "right"

  const InfoPanel = () => {
  const tt = useTranslations("booruDetailModal")
    if (!post) return null
    const hasDisputes = tagProposals.proposals.some((p) => p.kind === "remove")

    return (
      <InfoPanelView
                activeVideoUrl={activeVideoUrl}
                artists={artists}
                artistsOpen={artistsOpen}
                artistsTab={artistsTab}
                characters={characters}
                copyright={copyright}
                currentRating={currentRating}
                disputesOpen={disputesOpen}
                general={general}
                handleAddToVerticalEditor={handleAddToVerticalEditor}
                handleCreateScript={handleCreateScript}
                handleManualControl={handleManualControl}
                handlePlayScript={handlePlayScript}
                handleRequestBounty={handleRequestBounty}
                handleTagDispute={handleTagDispute}
                hasDisputes={hasDisputes}
                isAlbum={isAlbum}
                isMediaVideo={isMediaVideo}
                onNegativeTagClick={onNegativeTagClick}
                onOpenTagInBackgroundSet={onOpenTagInBackgroundSet}
                onOpenTagInNewSet={onOpenTagInNewSet}
                onTagClick={onTagClick}
                post={post}
                scriptsActiveTab={scriptsActiveTab}
                scriptsOpen={scriptsOpen}
                seriesOpen={seriesOpen}
                seriesTab={seriesTab}
                setActiveScript={setActiveScript}
                setArtistsOpen={setArtistsOpen}
                setArtistsTab={setArtistsTab}
                setDisputesOpen={setDisputesOpen}
                setRating={setRating}
                setScriptsActiveTab={setScriptsActiveTab}
                setScriptsOpen={setScriptsOpen}
                setSeriesOpen={setSeriesOpen}
                setSeriesTab={setSeriesTab}
                setShowComments={setShowComments}
                setShowScriptPlayer={setShowScriptPlayer}
                setTagsOpen={setTagsOpen}
                setTagsTab={setTagsTab}
                showScriptPlayer={showScriptPlayer}
                tagProposals={tagProposals}
                tagsArray={tagsArray}
                tagsOpen={tagsOpen}
                tagsTab={tagsTab}
                videoRef={videoRef}
                videoSyncSuppressRef={videoSyncSuppressRef}
              />
    )
  }

  // Download (single file and album as ZIP): components/booru-modal/use-modal-downloads.ts
  const { handleDownload, handleDownloadAllAsZip, isZipDownloading, zipDownloadProgress } = useModalDownloads({
    post, isAlbum, currentAlbumImageUrl, currentAlbumPage, effectiveAlbumImages, autoDownloadWithoutDialog,
  })

  // Frame capture, reverse search and Find Source: components/booru-modal/use-frame-capture.ts
  const {
    capturedFrameUrl, capturedFrameDataUrl, isCapturing, findSource, setFindSource,
    handleReverseSearch, handleFindSource, handleCaptureFrame, handleClearFrame, resetCapture,
  } = useFrameCapture({ post, isMediaVideo, videoRef, mediaRootRef })

  const handleScriptSaved = (scriptId: string) => {
    // Optionally show success message
  }

  const formatTime = (seconds: number) => {
    // Handle very short durations (< 1 second)
    if (!Number.isFinite(seconds) || seconds < 0) {
      return "0:00"
    }
    
    if (seconds < 1) {
      return "< 1s"
    }
    
    const h = Math.floor(seconds / 3600)
    const m = Math.floor((seconds % 3600) / 60)
    const s = Math.floor(seconds % 60)
    return h > 0
      ? `${h}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`
      : `${m}:${s.toString().padStart(2, "0")}`
  }

  return (
    <>
      {post && (
        <Dialog open={open} onOpenChange={onOpenChange}>
          <DialogContent
            data-video-modal
            showCloseButton={false}
            className={cn(
              "p-0 gap-0 overflow-hidden border-0 grid-rows-[auto_1fr] [safe-area-inset-top:env(safe-area-inset-top)] [safe-area-inset-bottom:env(safe-area-inset-bottom)]",
              modalDialogLayout === "classic" && "max-w-[100vw] w-full h-dvh max-h-dvh",
              modalDialogLayout === "pinned" && "top-0 translate-y-0 max-w-[100vw] w-full h-dvh max-h-dvh",
              modalDialogLayout === "fullscreen" &&
                "inset-0 translate-x-0 translate-y-0 w-screen max-w-none sm:max-w-none h-screen h-dvh max-h-screen max-h-dvh rounded-none",
            )}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onTouchCancel={() => {
              setIsPanning(false)
              handleMediaLongPressEnd()
            }}
            onWheel={handleWheel}
            // InterstitialAd renders through its own createPortal (not
            // Radix's Dialog.Portal), so Radix's own outside-interaction
            // detection sees a click on the ad's close button/backdrop as
            // "outside" this dialog and closes the whole gallery along with
            // it. Ignore exactly that one case — anything actually outside
            // the ad keeps closing the dialog like normal.
            onInteractOutside={(e) => {
              if ((e.target as HTMLElement | null)?.closest?.("[data-interstitial-ad-portal]")) {
                e.preventDefault()
              }
            }}
          >
            <DialogHeader>
              <DialogTitle className="sr-only">
                {post.title ? `${post.title} - ${post.site ?? "Post"}` : `Post ${post.id}`}
              </DialogTitle>
            </DialogHeader>
            {/* Custom close button — replaces the default DialogClose, which relies on
                text-muted-foreground and no backdrop and was effectively invisible over media
                content. Sits mostly out of the way at rest (~35% opacity — was 10%, too dim to
                find at a glance) and brightens to fully solid on hover/focus/tap, so it's still
                discoverable without competing with the media for attention the rest of the time. */}
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="absolute top-4 right-4 z-30 h-8 w-8 rounded-full flex items-center justify-center bg-black/70 text-white opacity-35 hover:opacity-100 focus-visible:opacity-100 active:opacity-100 transition-opacity pointer-events-auto"
              title={tt("close")}
            >
              <X className="h-4 w-4" />
              <span className="sr-only">{tt("close")}</span>
            </button>
            <div
              ref={mediaRootRef}
              className="relative w-full h-full min-h-0 bg-black flex items-center justify-center overflow-hidden px-4"
              onClick={(e) => {
                if (e.target === e.currentTarget) {
                  setHideUI(!hideUI)
                }
              }}
            >
              <AlbumPageCounter
                currentAlbumPage={currentAlbumPage}
                hideUI={hideUI}
                isAlbum={isAlbum}
                totalAlbumPages={totalAlbumPages}
                viewingAdSlot={viewingAdSlot}
              />

              {/* Active Script Overlay — self-contained, no modal re-renders */}
              <ScriptActiveOverlay hideUI={hideUI} />

              <TopToolbar
                activeScriptId={activeScriptId}
                activeScriptName={activeScriptName}
                activeVideoUrl={activeVideoUrl}
                bountySelectionActive={bountySelectionActive}
                capturedFrameDataUrl={capturedFrameDataUrl}
                checkIsFavorite={checkIsFavorite}
                closeModalSurfaces={closeModalSurfaces}
                commissionSelectionActive={commissionSelectionActive}
                effectiveAlbumImages={effectiveAlbumImages}
                fetchedDoujinsTitle={fetchedDoujinsTitle}
                getEnrichedPost={getEnrichedPost}
                handleAddToBountySelection={handleAddToBountySelection}
                handleAddToCommissionSelection={handleAddToCommissionSelection}
                handleAddToScript={handleAddToScript}
                handleCaptureFrame={handleCaptureFrame}
                handleClearFrame={handleClearFrame}
                handleDownload={handleDownload}
                handleDownloadAllAsZip={handleDownloadAllAsZip}
                handleFavoriteClick={handleFavoriteClick}
                handleFindSimilar={handleFindSimilar}
                handleOpenPip={handleOpenPip}
                handleReverseSearch={handleReverseSearch}
                handleFindSource={handleFindSource}
                handleShare={handleShare}
                hideUI={hideUI}
                imageDisplayMode={imageDisplayMode}
                isAddToScriptsDropdownOpen={isAddToScriptsDropdownOpen}
                isAlbum={isAlbum}
                isCapturing={isCapturing}
                isFindSimilarDropdownOpen={isFindSimilarDropdownOpen}
                isGoonfolioDropdownOpen={isGoonfolioDropdownOpen}
                isLinkingMode={isLinkingMode}
                isMediaVideo={isMediaVideo}
                isResolvingGalleryContent={isResolvingGalleryContent}
                isSlideshowActive={isSlideshowActive}
                isZipDownloading={isZipDownloading}
                linkingTargetIsGooncave={linkingTargetIsGooncave}
                openEditorsAfterSelection={openEditorsAfterSelection}
                post={post}
                setImageDisplayMode={setImageDisplayMode}
                setIsAddToScriptsDropdownOpen={setIsAddToScriptsDropdownOpen}
                setIsFindSimilarDropdownOpen={setIsFindSimilarDropdownOpen}
                setIsGoonfolioDropdownOpen={setIsGoonfolioDropdownOpen}
                setIsSlideshowActive={setIsSlideshowActive}
                setShowInfo={setShowInfo}
                setShowSaveToGoonfolio={setShowSaveToGoonfolio}
                setSlideshowInterval={setSlideshowInterval}
                setSlideshowShuffle={setSlideshowShuffle}
                showInfo={showInfo}
                slideshowInterval={slideshowInterval}
                slideshowShuffle={slideshowShuffle}
                topToolbarRef={topToolbarRef}
                zipDownloadProgress={zipDownloadProgress}
              />

              {/* Audio Controls Bar for Doujins.com audio doujins */}
              <AudioControlsBar
                audioManager={audioManager}
                audioProgress={audioProgress}
                audioVolume={audioVolume}
                hasAudio={hasAudio}
                isAudioMode={isAudioMode}
                isAudioMuted={isAudioMuted}
                isAudioPlaying={isAudioPlaying}
                setAudioVolume={setAudioVolume}
                setIsAudioMode={setIsAudioMode}
                setIsAudioMuted={setIsAudioMuted}
                setIsAudioPlaying={setIsAudioPlaying}
              />

              {/* Premium Content Alert - Show for Doujins premium galleries */}
              <PremiumContentAlert
                hideUI={hideUI}
                post={post}
                setShowPremiumModal={setShowPremiumModal}
              />

              {/* Post navigation arrows (only show for non-album or at album boundaries) */}
              <PostNavArrows
                canNavigate={canNavigate}
                hideUI={hideUI}
                isAlbum={isAlbum}
                onNavigate={onNavigate}
              />

              {/* Album page navigation - side buttons */}
              <AlbumSideButtons
                currentAlbumPage={currentAlbumPage}
                handleAlbumNextPage={handleAlbumNextPage}
                handleAlbumPrevPage={handleAlbumPrevPage}
                hideUI={hideUI}
                isAlbum={isAlbum}
                totalAlbumPages={totalAlbumPages}
                viewingAdSlot={viewingAdSlot}
              />

              {/* Resolving a lazy album's real pages. Until this finishes the
                  view falls back to post.fileUrl, which for an erome/doujins
                  placeholder is its /thumbs/ URL — i.e. a visibly low-res
                  image with, previously, nothing to say it was still loading.
                  It just looked like a broken thumbnail. */}
              {isResolvingGalleryContent && (
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-30 flex items-center gap-2 rounded-full bg-black/70 px-3 py-1.5 pointer-events-none">
                  <Loader2 className="h-3.5 w-3.5 animate-spin text-white/80" />
                  <span className="text-xs text-white/80">{tt("loadingFullResolution")}</span>
                </div>
              )}

              {/* Main image/video display area */}
              <div
                data-media-container
                ref={setMediaContainerRef}
                className={`relative w-full h-full flex items-center justify-center pointer-events-auto ${imageDisplayMode === 'cover' ? 'overflow-auto' : 'overflow-hidden'}`}
                style={{
                  touchAction:
                    // Only disable touch action when we're actually zoomed in or actively using video zoom OR in cover mode
                    (enableMousePan && (zoom > 1 || imageDisplayMode === 'cover')) || (isMediaVideo && isVideoZoomEnabled && zoom > 1)
                      ? "none"
                      : ("auto" as const),
                }}
                onMouseDown={handleMouseDown}
                onMouseUp={handleMouseUp}
                onMouseLeave={() => {
                  if (isPanning) setIsPanning(false)
                  handleMediaLongPressEnd()
                }}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                onTouchCancel={() => {
                  setIsPanning(false)
                  handleMediaLongPressEnd()
                }}
              >
                <MediaDisplayArea
                activeVideoUrl={activeVideoUrl}
                allowVideoZoom={allowVideoZoom}
                autoplayVideos={autoplayVideos}
                currentAlbumImageUrl={currentAlbumImageUrl}
                currentAlbumPage={currentAlbumPage}
                enableMousePan={enableMousePan}
                getCurrentVideoUrl={getCurrentVideoUrl}
                getMediaUrl={getMediaUrl}
                getPreferredMediaUrl={getPreferredMediaUrl}
                hasGifTag={hasGifTag}
                hideUI={hideUI}
                imageDisplayMode={imageDisplayMode}
                isAlbum={isAlbum}
                isCurrentAlbumPageVideo={isCurrentAlbumPageVideo}
                isLoadingFullImage={isLoadingFullImage}
                isLooping={isLooping}
                isMediaVideo={isMediaVideo}
                isMuted={isMuted}
                isPanning={isPanning}
                isVideoLoading={isVideoLoading}
                lazyLoadedEmbedUrl={lazyLoadedEmbedUrl}
                lazyLoadedVideoUrl={lazyLoadedVideoUrl}
                panX={panX}
                panY={panY}
                post={post}
                setContainerAR={setContainerAR}
                setContainerH={setContainerH}
                setCurrentAlbumPage={setCurrentAlbumPage}
                setCurrentTime={setCurrentTime}
                setDuration={setDuration}
                setHideUI={setHideUI}
                setIsVideoLoading={setIsVideoLoading}
                setMediaNativeAR={setMediaNativeAR}
                setShouldFallbackToImage={setShouldFallbackToImage}
                setTriedExtensions={setTriedExtensions}
                setVideoError={setVideoError}
                setVideoExtensionIndex={setVideoExtensionIndex}
                setViewingAdSlot={setViewingAdSlot}
                shouldFallbackToImage={shouldFallbackToImage}
                totalAlbumPages={totalAlbumPages}
                triedExtensions={triedExtensions}
                videoError={videoError}
                videoExtensionIndex={videoExtensionIndex}
                videoExtensions={videoExtensions}
                videoPitchCorrection={videoPitchCorrection}
                videoRef={videoRef}
                viewingAdSlot={viewingAdSlot}
                zoom={zoom}
              />

                {/* Letterbox ads — top/bottom bars, mobile-portrait case (H5/H7:
                    media wider than its frame). Works for images too now, not
                    just video — the geometry that creates these bars is the
                    same either way. Two real, distinct zones (tier 3 bottom,
                    tier 2 top — see letterboxTopAdData's comment) rather than
                    one zone rendered twice; dismissing either bar dismisses
                    both, since they're one ad experience split across two
                    slots, not two independent ads. */}
                {!isAlbum && !effectivePremiumUser && !letterboxAdDismissed && mediaNativeAR && containerAR && (
                  letterboxAdData?.bannerImageUrl || letterboxAdData?.videoAdUrl || letterboxAdData?.embedHtml
                ) && (
                  <LetterboxAd
                    position="bottom"
                    videoAspectRatio={mediaNativeAR}
                    containerAspectRatio={containerAR}
                    containerHeightPx={containerH}
                    bannerImageUrl={letterboxAdData?.bannerImageUrl}
                    bannerClickUrl={letterboxAdData?.bannerClickUrl}
                    videoAdUrl={letterboxAdData?.videoAdUrl}
                    embedHtml={letterboxAdData?.embedHtml}
                    zoneId={letterboxAdData?.zoneId}
                    {...(letterboxAdData?.creativeWidth && letterboxAdData?.creativeHeight
                      ? { creativeWidth: letterboxAdData.creativeWidth, creativeHeight: letterboxAdData.creativeHeight }
                      : {})}
                    className={letterboxFadeClass}
                    onDismiss={() => setLetterboxAdDismissed(true)}
                  />
                )}
                {!isAlbum && !effectivePremiumUser && !letterboxAdDismissed && mediaNativeAR && containerAR && (
                  letterboxTopAdData?.bannerImageUrl || letterboxTopAdData?.videoAdUrl || letterboxTopAdData?.embedHtml
                ) && (
                  <LetterboxAd
                    position="top"
                    videoAspectRatio={mediaNativeAR}
                    containerAspectRatio={containerAR}
                    containerHeightPx={containerH}
                    bannerImageUrl={letterboxTopAdData?.bannerImageUrl}
                    bannerClickUrl={letterboxTopAdData?.bannerClickUrl}
                    videoAdUrl={letterboxTopAdData?.videoAdUrl}
                    embedHtml={letterboxTopAdData?.embedHtml}
                    zoneId={letterboxTopAdData?.zoneId}
                    {...(letterboxTopAdData?.creativeWidth && letterboxTopAdData?.creativeHeight
                      ? { creativeWidth: letterboxTopAdData.creativeWidth, creativeHeight: letterboxTopAdData.creativeHeight }
                      : {})}
                    className={letterboxFadeClass}
                    onDismiss={() => setLetterboxAdDismissed(true)}
                  />
                )}

                {/* Pillarbox ads — side gutters, desktop-wide-viewport case
                    (H8: media narrower than its frame). Mutually exclusive
                    with LetterboxAd by construction — each checks the
                    opposite geometry internally and no-ops otherwise. Both
                    gutters render now, each a genuinely distinct zone (see
                    pillarboxLeftAdData's comment) — dismissing either
                    dismisses both, same "one ad experience, two slots"
                    convention as the letterbox bars. */}
                {!isAlbum && !effectivePremiumUser && !pillarboxAdDismissed && mediaNativeAR && containerAR && (
                  pillarboxAdData?.bannerImageUrl || pillarboxAdData?.embedHtml
                ) && (
                  <PillarboxAd
                    side="right"
                    mediaAspectRatio={mediaNativeAR}
                    containerAspectRatio={containerAR}
                    containerHeightPx={containerH}
                    bannerImageUrl={pillarboxAdData?.bannerImageUrl}
                    bannerClickUrl={pillarboxAdData?.bannerClickUrl}
                    embedHtml={pillarboxAdData?.embedHtml}
                    zoneId={pillarboxAdData?.zoneId}
                    {...(pillarboxAdData?.creativeWidth && pillarboxAdData?.creativeHeight
                      ? { creativeWidth: pillarboxAdData.creativeWidth, creativeHeight: pillarboxAdData.creativeHeight }
                      : {})}
                    className={pillarboxFadeClass}
                    onDismiss={() => setPillarboxAdDismissed(true)}
                  />
                )}
                {!isAlbum && !effectivePremiumUser && !pillarboxAdDismissed && mediaNativeAR && containerAR && (
                  pillarboxLeftAdData?.bannerImageUrl || pillarboxLeftAdData?.embedHtml
                ) && (
                  <PillarboxAd
                    side="left"
                    mediaAspectRatio={mediaNativeAR}
                    containerAspectRatio={containerAR}
                    containerHeightPx={containerH}
                    bannerImageUrl={pillarboxLeftAdData?.bannerImageUrl}
                    bannerClickUrl={pillarboxLeftAdData?.bannerClickUrl}
                    embedHtml={pillarboxLeftAdData?.embedHtml}
                    zoneId={pillarboxLeftAdData?.zoneId}
                    {...(pillarboxLeftAdData?.creativeWidth && pillarboxLeftAdData?.creativeHeight
                      ? { creativeWidth: pillarboxLeftAdData.creativeWidth, creativeHeight: pillarboxLeftAdData.creativeHeight }
                      : {})}
                    className={pillarboxFadeClass}
                    onDismiss={() => setPillarboxAdDismissed(true)}
                  />
                )}

                {/* Gallery ad slot removed — will be re-added via wrapper component */}

                {showPauseOverlayAd && (
                  <PauseOverlayAd onDismiss={() => setShowPauseOverlayAd(false)} />
                )}

                {/* Video pre-roll — mounted INSIDE the media container (which
                    is `relative`), so `inline` makes it fill the video frame
                    rather than the viewport. It used to be a sibling of
                    DialogContent rendering `fixed inset-0`, i.e. a full-screen
                    blackout of the whole app for every pre-roll. A pre-roll
                    belongs in the player, the way it does on YouTube and every
                    other video site — and the full-screen version was actively
                    harmful with non-skippable ads enabled, since it took the
                    entire UI hostage rather than just the video. */}
                {showVideoPreroll && (
                  <VideoPreroll
                    inline
                    vastUrl={prerollVastUrl ?? undefined}
                    preresolved={prerollPreresolved ?? undefined}
                    zoneId={prerollZoneId}
                    skipAfterSecs={useAdStore.getState().unskippableDuration}
                    maxDurationSecs={30}
                    onComplete={() => { setShowVideoPreroll(false); warmVideoPreroll(modalAdTier); markRealAdWatchedToday() }}
                    onSkip={() => { setShowVideoPreroll(false); warmVideoPreroll(modalAdTier); markRealAdWatchedToday() }}
                  />
                )}
              </div>

              {/* Thumbnail strip for albums - REDESIGNED */}
              <AlbumThumbnailStrip
                albumPages={albumPages}
                albumStripRef={albumStripRef}
                albumThumbnailsRef={albumThumbnailsRef}
                currentAlbumPage={currentAlbumPage}
                getMediaUrl={getMediaUrl}
                handleAlbumAdSlotClick={handleAlbumAdSlotClick}
                handleAlbumPageClick={handleAlbumPageClick}
                hideUI={hideUI}
                isAlbum={isAlbum}
                post={post}
                viewingAdSlot={viewingAdSlot}
              />

              {/* Device XY Control Pad overlay */}
              {showDeviceXYPad && (
                <DeviceXYPad onClose={() => setShowDeviceXYPad(false)} />
              )}

              {/* Video controls overlay - show for videos AND album pages with videos */}
              <VideoControlsOverlay
                abLoopEnabled={abLoopEnabled}
                activeVideoUrl={activeVideoUrl}
                currentAlbumPage={currentAlbumPage}
                duration={duration}
                formatTime={formatTime}
                frameNavEnabled={frameNavEnabled}
                getCurrentTime={getCurrentTime}
                handleAlbumNextPage={handleAlbumNextPage}
                handleAlbumPrevPage={handleAlbumPrevPage}
                handleFrameBackward={handleFrameBackward}
                handleFrameForward={handleFrameForward}
                handlePlayPause={handlePlayPause}
                handleVrScriptSelect={handleVrScriptSelect}
                isAlbum={isAlbum}
                isLooping={isLooping}
                isMuted={isMuted}
                isPlaying={isPlaying}
                isVideoZoomEnabled={isVideoZoomEnabled}
                loopPointA={loopPointA}
                loopPointB={loopPointB}
                playbackSpeed={playbackSpeed}
                playhead={playhead.current!}
                setAbLoopEnabled={setAbLoopEnabled}
                setCurrentTime={setCurrentTime}
                setFrameNavEnabled={setFrameNavEnabled}
                setIsLooping={setIsLooping}
                setIsMuted={setIsMuted}
                setIsVideoZoomEnabled={setIsVideoZoomEnabled}
                setLoopPointA={setLoopPointA}
                setLoopPointB={setLoopPointB}
                setPanX={setPanX}
                setPanY={setPanY}
                setPlaybackSpeed={setPlaybackSpeed}
                setShowVolumePopup={setShowVolumePopup}
                setVideoPitchCorrection={setVideoPitchCorrection}
                setVolume={setVolume}
                setVrSyncDelay={setVrSyncDelay}
                setZoom={setZoom}
                showVideoHud={showVideoHud}
                showVolumePopup={showVolumePopup}
                totalAlbumPages={totalAlbumPages}
                userChangedMuteRef={userChangedMuteRef}
                videoHudBottomPx={videoHudBottomPx}
                videoPitchCorrection={videoPitchCorrection}
                videoRef={videoRef}
                viewingAdSlot={viewingAdSlot}
                volume={volume}
                volumePopupRef={volumePopupRef}
                vrActiveScript={vrActiveScript}
                vrSyncDelay={vrSyncDelay}
              />

              {/* Device Status Indicator */}
              <DeviceStatusIndicator
                activeScriptId={activeScriptId}
                activeScriptName={activeScriptName}
                connectedDeviceName={connectedDeviceName}
                hideUI={hideUI}
                isDeviceConnected={isDeviceConnected}
                setShowQuickConnect={setShowQuickConnect}
              />
              <QuickConnectDeviceModal open={showQuickConnect} onClose={() => setShowQuickConnect(false)} />

              {/* Zoom controls for non-video, non-album */}
              <ZoomControls
                canNavigate={canNavigate}
                fullscreen={fullscreen}
                handleBottomSwipeTouchEnd={handleBottomSwipeTouchEnd}
                handleBottomSwipeTouchStart={handleBottomSwipeTouchStart}
                handleZoomIn={handleZoomIn}
                handleZoomOut={handleZoomOut}
                hideUI={hideUI}
                isAlbum={isAlbum}
                isCurrentAlbumPageVideo={isCurrentAlbumPageVideo}
                isMediaVideo={isMediaVideo}
                onNavigate={onNavigate}
                setZoom={setZoom}
                zoom={zoom}
              />

              <AlbumNavBar
                albumNavBottomPx={albumNavBottomPx}
                currentAlbumPage={currentAlbumPage}
                handleAlbumNextPage={handleAlbumNextPage}
                handleAlbumPrevPage={handleAlbumPrevPage}
                handleBottomSwipeTouchEnd={handleBottomSwipeTouchEnd}
                handleBottomSwipeTouchStart={handleBottomSwipeTouchStart}
                handleZoomIn={handleZoomIn}
                handleZoomOut={handleZoomOut}
                setZoom={setZoom}
                showAlbumNav={showAlbumNav}
                totalAlbumPages={totalAlbumPages}
                viewingAdSlot={viewingAdSlot}
                zoom={zoom}
              />
            </div>
          </DialogContent>
          <DoujinsPremiumModal isOpen={showPremiumModal} onClose={() => setShowPremiumModal(false)} />
          {/* Video pre-roll now renders in-stream, inside the media container
              above — not here as a full-screen sibling of the dialog. */}
          {/* Full-screen interstitial ad — renders at z-[9999], well above the
              VR overlay's z-[60], so without this guard it bleeds straight
              through the immersive canvas. No ads at all in VR mode. */}
          {showInterstitial && !isVRMode && <InterstitialAd kind="network" onClose={dismissInterstitial} />}
          {showHouseInterstitial && !isVRMode && <InterstitialAd kind="house" onClose={dismissHouseInterstitial} />}
          {/* Add Report Content Dialog */}
          {post && (
            <ReportContentDialog
              open={showReportDialog}
              onOpenChange={setShowReportDialog}
              contentType="tag"
              contentId={`${post.site}:${post.id}`}
              contentTitle={`Post #${post.id} from ${post.site}`}
            />
          )}
          <FindSourceDialog
            open={findSource.open}
            onOpenChange={(open) => setFindSource((prev) => ({ ...prev, open }))}
            loading={findSource.loading}
            matches={findSource.matches}
            errors={findSource.errors}
          />
          {post && (
            <SaveToGoonfolioDialog
              open={showSaveToGoonfolio}
              onOpenChange={setShowSaveToGoonfolio}
              item={{ itemType: "external_content", externalContent: post }}
            />
          )}
        </Dialog>
      )}

      <Sheet open={showInfo} onOpenChange={setShowInfo}>
        <SheetContent side={sheetSide} className="w-full sm:w-[26rem] p-0 flex flex-col h-full">
          <SheetHeader className="p-6 pb-4">
            <SheetTitle>{tt("postDetails")}</SheetTitle>
          </SheetHeader>
          <div className="flex-1 min-h-0">
            {/* Called as a plain function, not rendered as <InfoPanel />: InfoPanel is
                redefined on every parent render, so as a JSX element type it would look
                like a brand-new component each time, forcing React to unmount/remount
                the whole subtree (including its ScrollArea, resetting scroll to the top)
                on every parent state change — e.g. switching the Scripts/My Scripts/
                Device sub-tabs inside it. Calling it inline keeps the same JSX tree
                position across renders so React just diffs it normally. */}
            {InfoPanel()}
          </div>
        </SheetContent>
      </Sheet>

      <Sheet open={showComments} onOpenChange={setShowComments}>
        <SheetContent side={sheetSide} className="w-full sm:w-[26rem] p-0 flex flex-col h-full">
          <SheetHeader className="p-6 pb-4">
            <SheetTitle>{tt("comments")}</SheetTitle>
          </SheetHeader>
          <div className="flex-1 min-h-0">
            {post?.id && post.site && <CommentsPanel postSite={post.site} postId={String(post.id)} />}
          </div>
        </SheetContent>
      </Sheet>

      {post?.id && <DisputesPanel contentId={`${post.site}:${post.id}`} />}
    </>
  )
}

const getRatingColor = (rating: string) => {
  switch (rating) {
    case "safe":
      return "bg-green-500/10 text-green-500 border-green-500/20"
    case "questionable":
      return "bg-yellow-500/10 text-yellow-500 border-yellow-500/20"
    case "explicit":
      return "bg-red-500/10 text-red-500 border-red-500/20"
    default:
      return ""
  }
}
